/**
 * RoadmapTab V3 — GitHub Issues Live-Sync + Feedback + Handlungsempfehlungen
 * 
 * Features:
 * - Erweiterte Filter: Projekt, Status, Priorität, Agent, Typ
 * - Screenshot-Paste (Strg+V) in allen Textfeldern
 * - Aktive Projekte mit grünem Leuchtpunkt + Puls-Animation + Fortschritt
 * - Handlungsempfehlungen nach Claude-Aktionen (5 Vorschläge)
 * - Anforderungs-Bereich: Wenn Claude etwas braucht (Anmeldung, Daten, Dokumente)
 * - Issue-Karten mit Labels, Agent-Zuordnung, Checkliste
 * - Feedback mit Text + Screenshot-Upload + Paste
 * - Neues Issue erstellen aus Feedback
 * - Manus-Dev-Agent Integration
 * - Live-Sync mit GitHub
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  RefreshCw, Check, X, ChevronDown, ChevronUp,
  Loader2, Sparkles, Plus, Send,
  Lightbulb, Target, Rocket, Eye, CheckCircle2,
  Bug, Wrench, FileText, ExternalLink,
  MessageCircle, Github, Camera, ImageIcon,
  Bot, AlertTriangle, Paperclip, Hash,
  Upload, Filter, Car, Globe, Diamond,
  FileSearch, Plug, Server, Clipboard,
  ArrowRight, Zap, Clock, UserCheck,
  FileUp, Key, Info, Star,
  SlidersHorizontal, LayoutGrid, Play, Pause,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import {
  useGitHubRoadmap,
  GITHUB_PROJEKTE,
  type GitHubIssue,
  type IssueFeedback,
  type RoadmapStatus,
  type RoadmapPrioritaet,
  type RoadmapTyp,
  type GitHubKommentar,
} from '@/hooks/useGitHubRoadmap';

// ═══════════════════════════════════════════════════════════════════════════
// CI DESIGN TOKENS
// ═══════════════════════════════════════════════════════════════════════════

const CI = {
  red: '#C41E3A',
  redDark: '#9B1C1C',
  redLight: '#FEF2F2',
  redBorder: '#FECACA',
  bgPanel: '#FFFFFF',
  bgSection: '#F9FAFB',
  bgZebraEven: '#FFFFFF',
  bgZebraOdd: '#F8F9FB',
  bgHover: '#F3F4F6',
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textMuted: '#9CA3AF',
  textLight: '#D1D5DB',
  border: '#E5E7EB',
  borderLight: '#F3F4F6',
  shadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
  shadowHover: '0 4px 12px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.06)',
  emerald: '#059669',
  emeraldBg: '#ECFDF5',
  amber: '#D97706',
  amberBg: '#FFFBEB',
  blue: '#2563EB',
  blueBg: '#EFF6FF',
  purple: '#7C3AED',
  purpleBg: '#F5F3FF',
  cyan: '#0891B2',
  cyanBg: '#ECFEFF',
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// STATUS + PRIORITÄT + TYP KONFIGURATIONEN
// ═══════════════════════════════════════════════════════════════════════════

const STATUS_CONFIG: Record<RoadmapStatus, { label: string; farbe: string; bg: string; icon: React.ElementType }> = {
  idee:           { label: 'Idee',      farbe: CI.textSecondary, bg: CI.bgSection,  icon: Lightbulb },
  geplant:        { label: 'Geplant',   farbe: CI.blue,          bg: CI.blueBg,     icon: Target },
  in_arbeit:      { label: 'In Arbeit', farbe: CI.amber,         bg: CI.amberBg,    icon: Rocket },
  review:         { label: 'Review',    farbe: CI.purple,        bg: CI.purpleBg,   icon: Eye },
  human_feedback: { label: 'Feedback',  farbe: CI.red,           bg: CI.redLight,   icon: UserCheck },
  fertig:         { label: 'Fertig',    farbe: CI.emerald,       bg: CI.emeraldBg,  icon: CheckCircle2 },
};

const PRIO_CONFIG: Record<RoadmapPrioritaet, { label: string; farbe: string; bg: string }> = {
  critical: { label: 'Kritisch', farbe: '#DC2626', bg: '#FEF2F2' },
  high:     { label: 'Hoch',     farbe: CI.amber,  bg: CI.amberBg },
  medium:   { label: 'Mittel',   farbe: CI.blue,   bg: CI.blueBg },
  low:      { label: 'Niedrig',  farbe: CI.textMuted, bg: CI.bgSection },
};

const TYP_CONFIG: Record<RoadmapTyp, { label: string; icon: React.ElementType; farbe: string }> = {
  feature:  { label: 'Feature',  icon: Rocket,   farbe: CI.blue },
  bug:      { label: 'Bug',      icon: Bug,      farbe: '#DC2626' },
  refactor: { label: 'Refactor', icon: Wrench,   farbe: CI.amber },
  research: { label: 'Research', icon: FileSearch,farbe: CI.purple },
  docs:     { label: 'Docs',     icon: FileText,  farbe: CI.cyan },
};

const AGENT_CONFIG: Record<string, { label: string; icon: React.ElementType; farbe: string; bg: string }> = {
  manus:        { label: 'Manus',       icon: Bot,      farbe: CI.purple, bg: CI.purpleBg },
  'claude-code':{ label: 'Claude Code', icon: Github,   farbe: CI.blue,   bg: CI.blueBg },
  unassigned:   { label: 'Offen',       icon: Sparkles, farbe: CI.textMuted, bg: CI.bgSection },
};

const PROJEKT_ICONS: Record<string, React.ElementType> = {
  'gutachter-koop-app': Rocket,
  'unfallgiganten': Car,
  'flooas': Globe,
  'winvalue-automation': FileSearch,
  'gutachter-koop-website': Globe,
  'gutachter-koop-mcp': Server,
  'lenneglas': Diamond,
  'mietgo': Key,
};

// ═══════════════════════════════════════════════════════════════════════════
// HANDLUNGSEMPFEHLUNGEN-SYSTEM
// ═══════════════════════════════════════════════════════════════════════════

interface Handlungsempfehlung {
  id: string;
  text: string;
  icon: React.ElementType;
  farbe: string;
  bg: string;
  typ: 'aktion' | 'info' | 'anforderung';
  projekt?: string;
  issueNumber?: number;
}

function generateHandlungsempfehlungen(issues: GitHubIssue[]): Handlungsempfehlung[] {
  const empfehlungen: Handlungsempfehlung[] = [];

  // 1. Issues die Review brauchen
  const reviewIssues = issues.filter(i => i.status === 'review');
  for (const issue of reviewIssues.slice(0, 2)) {
    empfehlungen.push({
      id: `review-${issue.id}`,
      text: `Review ausstehend: "${issue.title}" — Bitte prüfen und Feedback geben`,
      icon: Eye,
      farbe: CI.purple,
      bg: CI.purpleBg,
      typ: 'aktion',
      projekt: issue.projekt,
      issueNumber: issue.number,
    });
  }

  // 2. Issues mit hoher Priorität die noch Idee sind
  const criticalIdeen = issues.filter(i => i.prioritaet === 'critical' && i.status === 'idee');
  for (const issue of criticalIdeen.slice(0, 1)) {
    empfehlungen.push({
      id: `critical-${issue.id}`,
      text: `Kritisches Feature "${issue.title}" noch nicht gestartet — Soll Claude Code beginnen?`,
      icon: AlertTriangle,
      farbe: '#DC2626',
      bg: CI.redLight,
      typ: 'aktion',
      projekt: issue.projekt,
      issueNumber: issue.number,
    });
  }

  // 3. Aktive Projekte mit Fortschritt
  const inArbeit = issues.filter(i => i.status === 'in_arbeit' && i.fortschritt > 0 && i.fortschritt < 100);
  for (const issue of inArbeit.slice(0, 2)) {
    empfehlungen.push({
      id: `progress-${issue.id}`,
      text: `"${issue.title}" ist bei ${issue.fortschritt}% — ${issue.fortschritt > 70 ? 'Fast fertig! Feedback vorbereiten.' : 'Fortschritt prüfen und ggf. Feedback geben.'}`,
      icon: Rocket,
      farbe: CI.amber,
      bg: CI.amberBg,
      typ: 'info',
      projekt: issue.projekt,
      issueNumber: issue.number,
    });
  }

  // 4. Issues mit Feedback das noch offen ist
  const mitFeedback = issues.filter(i => i.feedbacks.length > 0 && i.status !== 'fertig');
  for (const issue of mitFeedback.slice(0, 1)) {
    empfehlungen.push({
      id: `feedback-${issue.id}`,
      text: `${issue.feedbacks.length} Feedback(s) zu "${issue.title}" — Wurde das umgesetzt?`,
      icon: MessageCircle,
      farbe: CI.blue,
      bg: CI.blueBg,
      typ: 'aktion',
      projekt: issue.projekt,
      issueNumber: issue.number,
    });
  }

  // 5. Projekte ohne aktive Issues
  const aktiveProjekte = new Set(issues.filter(i => i.status === 'in_arbeit').map(i => i.projekt));
  const inaktiveProjekte = GITHUB_PROJEKTE.filter(p => !aktiveProjekte.has(p.id) && issues.some(i => i.projekt === p.id));
  if (inaktiveProjekte.length > 0) {
    empfehlungen.push({
      id: 'inactive-projects',
      text: `${inaktiveProjekte.map(p => p.name).join(', ')} ${inaktiveProjekte.length === 1 ? 'hat' : 'haben'} keine laufenden Tasks — Offenes Issue aktivieren oder neues starten?`,
      icon: Zap,
      farbe: CI.cyan,
      bg: CI.cyanBg,
      typ: 'info',
    });
  }

  return empfehlungen.slice(0, 5);
}

// ═══════════════════════════════════════════════════════════════════════════
// ANFORDERUNGEN-SYSTEM (Was Claude braucht)
// ═══════════════════════════════════════════════════════════════════════════

interface Anforderung {
  id: string;
  text: string;
  icon: React.ElementType;
  typ: 'login' | 'daten' | 'dokument' | 'entscheidung' | 'zugang';
  projekt?: string;
  erledigt: boolean;
}

function generateAnforderungen(issues: GitHubIssue[]): Anforderung[] {
  const anforderungen: Anforderung[] = [];

  // Aus Issue-Bodies nach Anforderungen suchen
  for (const issue of issues.filter(i => i.status === 'in_arbeit' || i.status === 'review')) {
    const body = (issue.body || '').toLowerCase();

    if (body.includes('anmeldung') || body.includes('login') || body.includes('zugang') || body.includes('credentials')) {
      anforderungen.push({
        id: `login-${issue.id}`,
        text: `Zugang/Login benötigt für "${issue.title}"`,
        icon: Key,
        typ: 'login',
        projekt: issue.projekt,
        erledigt: false,
      });
    }
    if (body.includes('dokument') || body.includes('datei') || body.includes('upload') || body.includes('pdf')) {
      anforderungen.push({
        id: `doc-${issue.id}`,
        text: `Dokument/Datei benötigt für "${issue.title}"`,
        icon: FileUp,
        typ: 'dokument',
        projekt: issue.projekt,
        erledigt: false,
      });
    }
    if (body.includes('entscheidung') || body.includes('abstimmung') || body.includes('klärung')) {
      anforderungen.push({
        id: `decision-${issue.id}`,
        text: `Entscheidung nötig für "${issue.title}"`,
        icon: UserCheck,
        typ: 'entscheidung',
        projekt: issue.projekt,
        erledigt: false,
      });
    }
  }

  return anforderungen.slice(0, 5);
}

// ═══════════════════════════════════════════════════════════════════════════
// SCREENSHOT-PASTE HOOK (Strg+V)
// ═══════════════════════════════════════════════════════════════════════════

function useScreenshotPaste(onPaste: (file: File, preview: string) => void) {
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = () => {
            onPaste(file, reader.result as string);
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  }, [onPaste]);

  return { handlePaste };
}

// ═══════════════════════════════════════════════════════════════════════════
// FRAGEN-PARSER — Erkennt nummerierte Fragen + Optionen in Issue-Bodies
// ═══════════════════════════════════════════════════════════════════════════

interface ParsedFrage {
  id: number;
  titel: string;
  beschreibung: string;
  optionen: { label: string; icon: string }[];
  freitext: boolean;
}

function parseFragenAusBody(body: string): ParsedFrage[] {
  if (!body) return [];
  const fragen: ParsedFrage[] = [];
  
  // Pattern: "1. **Frage?**" oder "1. **Frage:**"
  const fragenRegex = /(?:^|\n)\s*(\d+)\.\s*\*\*([^*]+)\*\*\s*([\s\S]*?)(?=(?:\n\s*\d+\.\s*\*\*)|$)/g;
  let match;
  
  while ((match = fragenRegex.exec(body)) !== null) {
    const id = parseInt(match[1]);
    const titel = match[2].trim().replace(/[?:]$/, '').trim();
    const beschreibung = match[3].trim();
    
    // Optionen aus dem Text extrahieren ("- Option" oder "— Option")
    const optionen: { label: string; icon: string }[] = [];
    const optRegex = /[-–—]\s*(.+?)(?:\?|$)/gm;
    let optMatch;
    while ((optMatch = optRegex.exec(beschreibung)) !== null) {
      const raw = optMatch[1].trim();
      if (raw.length > 3 && raw.length < 120) {
        // Passendes Icon basierend auf Inhalt
        let icon = '📋';
        if (/upload|datei|drag|drop|file/i.test(raw)) icon = '📁';
        else if (/supabase|storage|cloud/i.test(raw)) icon = '☁️';
        else if (/winvalue|extern/i.test(raw)) icon = '🔄';
        else if (/ja|editierbar|alle/i.test(raw)) icon = '✅';
        else if (/nein|read.only|nicht/i.test(raw)) icon = '❌';
        else if (/bestimmte|teil|einige/i.test(raw)) icon = '📌';
        else if (/manuell|drag/i.test(raw)) icon = '✋';
        else if (/kategori|sortier|filter/i.test(raw)) icon = '🏷️';
        else if (/datum|zeit|name/i.test(raw)) icon = '📅';
        else if (/design|style|farb/i.test(raw)) icon = '🎨';
        else if (/api|code|tech/i.test(raw)) icon = '⚙️';
        else if (/sicher|auth|login/i.test(raw)) icon = '🔒';
        optionen.push({ label: raw, icon });
      }
    }
    
    // Wenn keine Optionen gefunden, versuche "oder" Patterns
    if (optionen.length === 0) {
      const oderParts = beschreibung.split(/\s+oder\s+|\s+—\s+/i);
      if (oderParts.length >= 2) {
        for (const part of oderParts) {
          const clean = part.replace(/^[^a-zA-ZäöüÄÖÜ]*/, '').replace(/[?.,;!]*$/, '').trim();
          if (clean.length > 3 && clean.length < 120) {
            optionen.push({ label: clean, icon: '📋' });
          }
        }
      }
    }
    
    // Immer Ja/Nein als Fallback wenn Frage mit ? endet und keine Optionen
    if (optionen.length === 0 && match[2].includes('?')) {
      optionen.push({ label: 'Ja', icon: '✅' });
      optionen.push({ label: 'Nein', icon: '❌' });
      optionen.push({ label: 'Teilweise', icon: '📌' });
    }
    
    fragen.push({
      id,
      titel,
      beschreibung: beschreibung.substring(0, 200),
      optionen,
      freitext: true,
    });
  }
  
  return fragen;
}

// ═══════════════════════════════════════════════════════════════════════════
// FRAGEN-BLOCK KOMPONENTE — Interaktive Antwort-Buttons
// ═══════════════════════════════════════════════════════════════════════════

function FragenBlock({
  fragen,
  onSubmit,
  issueNumber,
}: {
  fragen: ParsedFrage[];
  onSubmit: (antworten: string) => Promise<boolean>;
  issueNumber: number;
}) {
  const [antworten, setAntworten] = useState<Record<number, { auswahl: string; freitext: string }>>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const setAntwort = (frageId: number, auswahl: string) => {
    setAntworten(prev => ({
      ...prev,
      [frageId]: { ...prev[frageId], auswahl: prev[frageId]?.auswahl === auswahl ? '' : auswahl, freitext: prev[frageId]?.freitext || '' },
    }));
  };

  const setFreitext = (frageId: number, text: string) => {
    setAntworten(prev => ({
      ...prev,
      [frageId]: { ...prev[frageId], auswahl: prev[frageId]?.auswahl || '', freitext: text },
    }));
  };

  const beantwortet = fragen.filter(f => {
    const a = antworten[f.id];
    return a && (a.auswahl || a.freitext?.trim());
  }).length;

  const handleSubmit = async () => {
    setSending(true);
    // Formatiere alle Antworten als GitHub-Kommentar
    let kommentar = `## 📋 Antworten zu den Klärungsfragen\n\n`;
    for (const frage of fragen) {
      const a = antworten[frage.id];
      kommentar += `### ${frage.id}. ${frage.titel}\n`;
      if (a?.auswahl) {
        kommentar += `**Auswahl:** ${a.auswahl}\n`;
      }
      if (a?.freitext?.trim()) {
        kommentar += `**Anmerkung:** ${a.freitext.trim()}\n`;
      }
      if (!a?.auswahl && !a?.freitext?.trim()) {
        kommentar += `_Keine Antwort_\n`;
      }
      kommentar += `\n`;
    }
    kommentar += `---\n_Über Gutachter Koop Kommandozentrale — Interaktives Frage-Antwort-System_`;
    
    const ok = await onSubmit(kommentar);
    if (ok) {
      setSent(true);
      toast.success(`${beantwortet} Antworten zu #${issueNumber} gesendet`);
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="rounded-xl p-3 text-center" style={{ backgroundColor: CI.emeraldBg, border: `1px solid ${CI.emerald}30` }}>
        <CheckCircle2 className="w-5 h-5 mx-auto mb-1" style={{ color: CI.emerald }} />
        <p className="text-[11px] font-medium" style={{ color: CI.emerald }}>
          {beantwortet}/{fragen.length} Antworten gesendet
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: CI.red }}>
          <Zap className="w-3.5 h-3.5" />
          {fragen.length} Klärungsfragen — Bitte beantworten
        </p>
        <span className="text-[9px] font-mono px-2 py-0.5 rounded-full" style={{ backgroundColor: beantwortet === fragen.length ? CI.emeraldBg : CI.amberBg, color: beantwortet === fragen.length ? CI.emerald : CI.amber }}>
          {beantwortet}/{fragen.length}
        </span>
      </div>

      {/* Fragen-Blöcke */}
      {fragen.map(frage => {
        const a = antworten[frage.id];
        const istBeantwortet = a && (a.auswahl || a.freitext?.trim());
        return (
          <div
            key={frage.id}
            className="rounded-xl p-3 space-y-2 transition-all"
            style={{
              backgroundColor: istBeantwortet ? CI.emeraldBg : CI.bgPanel,
              border: `1px solid ${istBeantwortet ? CI.emerald + '40' : CI.border}`,
            }}
          >
            {/* Frage-Titel */}
            <div className="flex items-start gap-2">
              <span
                className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                style={{
                  backgroundColor: istBeantwortet ? CI.emerald : CI.red,
                  color: 'white',
                }}
              >
                {istBeantwortet ? '✓' : frage.id}
              </span>
              <div className="flex-1">
                <p className="text-[11px] font-semibold leading-snug" style={{ color: CI.textPrimary }}>
                  {frage.titel}
                </p>
                {frage.beschreibung && (
                  <p className="text-[10px] mt-0.5 leading-relaxed" style={{ color: CI.textMuted }}>
                    {frage.beschreibung.substring(0, 150)}{frage.beschreibung.length > 150 ? '...' : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Antwort-Buttons */}
            {frage.optionen.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pl-7">
                {frage.optionen.map((opt, i) => {
                  const isSelected = a?.auswahl === opt.label;
                  return (
                    <button
                      key={i}
                      onClick={() => setAntwort(frage.id, opt.label)}
                      className="text-[10px] px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1"
                      style={{
                        backgroundColor: isSelected ? CI.red : CI.bgSection,
                        color: isSelected ? 'white' : CI.textSecondary,
                        fontWeight: isSelected ? 600 : 400,
                        border: `1px solid ${isSelected ? CI.red : CI.border}`,
                        boxShadow: isSelected ? '0 2px 4px rgba(196,30,58,0.2)' : 'none',
                      }}
                    >
                      <span>{opt.icon}</span> {opt.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Freitext */}
            {frage.freitext && (
              <div className="pl-7">
                <input
                  value={a?.freitext || ''}
                  onChange={e => setFreitext(frage.id, e.target.value)}
                  placeholder="Eigene Antwort / Anmerkung..."
                  className="w-full text-[10px] px-2.5 py-1.5 rounded-lg focus:outline-none focus:ring-1"
                  style={{ backgroundColor: CI.bgSection, color: CI.textPrimary, border: `1px solid ${CI.borderLight}` }}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* Absenden-Button */}
      <button
        onClick={handleSubmit}
        disabled={beantwortet === 0 || sending}
        className="w-full flex items-center justify-center gap-2 text-[11px] font-semibold text-white py-2.5 rounded-xl transition-all disabled:opacity-40"
        style={{
          backgroundColor: beantwortet === fragen.length ? CI.emerald : CI.red,
          boxShadow: beantwortet > 0 ? '0 2px 8px rgba(196,30,58,0.25)' : 'none',
        }}
      >
        {sending ? (
          <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Wird gesendet...</>
        ) : (
          <><Send className="w-3.5 h-3.5" /> {beantwortet}/{fragen.length} Antworten bestätigen & absenden</>
        )}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ISSUE-KARTE
// ═══════════════════════════════════════════════════════════════════════════

function IssueKarte({
  issue,
  onFeedback,
  onUpdate,
  onFetchComments,
  onReply,
  index,
}: {
  issue: GitHubIssue;
  onFeedback: (issueId: string, text: string, typ: IssueFeedback['typ'], file?: File) => Promise<boolean>;
  onUpdate: (issueId: string, updates: { agent?: 'manus' | 'claude-code' | 'unassigned'; status?: RoadmapStatus; prioritaet?: RoadmapPrioritaet; typ?: RoadmapTyp; title?: string }) => Promise<boolean>;
  onFetchComments: (repo: string, issueNumber: number) => Promise<GitHubKommentar[]>;
  onReply: (issueId: string, text: string) => Promise<boolean>;
  index: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [kommentare, setKommentare] = useState<GitHubKommentar[]>([]);
  const [loadingKommentare, setLoadingKommentare] = useState(false);
  const [kommentareLaden, setKommentareLaden] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackTyp, setFeedbackTyp] = useState<IssueFeedback['typ']>('feedback');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(issue.title);
  const [openDropdown, setOpenDropdown] = useState<'status' | 'prio' | 'typ' | 'agent' | null>(null);
  const [updating, setUpdating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Dropdown schließen bei Klick außerhalb
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    if (openDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  // Kommentare laden wenn Issue expandiert wird
  useEffect(() => {
    if (expanded && !kommentareLaden) {
      setKommentareLaden(true);
      setLoadingKommentare(true);
      onFetchComments(issue.repo, issue.number)
        .then(k => setKommentare(k))
        .finally(() => setLoadingKommentare(false));
    }
  }, [expanded]);

  const hatOffeneFragen =
    issue.state !== 'closed' &&
    kommentare.some(k => k.body.startsWith('🤔 **Clarifying Questions**')) &&
    !kommentare.some(k => k.is_antwort);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    const ok = await onReply(issue.id, replyText);
    if (ok) {
      setReplyText('');
      toast.success('Antwort gepostet');
      const k = await onFetchComments(issue.repo, issue.number);
      setKommentare(k);
    } else {
      toast.error('Antwort konnte nicht gesendet werden');
    }
    setSendingReply(false);
  };

  const handleFieldUpdate = async (updates: Parameters<typeof onUpdate>[1]) => {
    setUpdating(true);
    setOpenDropdown(null);
    await onUpdate(issue.id, updates);
    setUpdating(false);
  };

  const handleTitleSave = async () => {
    if (titleDraft.trim() && titleDraft !== issue.title) {
      await handleFieldUpdate({ title: titleDraft.trim() });
    }
    setEditingTitle(false);
  };

  const statusCfg = STATUS_CONFIG[issue.status];
  const prioCfg = PRIO_CONFIG[issue.prioritaet];
  const typCfg = TYP_CONFIG[issue.typ];
  const agentCfg = AGENT_CONFIG[issue.agent];
  const StatusIcon = statusCfg.icon;
  const TypIcon = typCfg.icon;
  const AgentIcon = agentCfg.icon;
  const projektName = GITHUB_PROJEKTE.find(p => p.id === issue.projekt)?.name || issue.repo;
  const ProjektIcon = PROJEKT_ICONS[issue.projekt] || Rocket;
  const isActive = issue.status === 'in_arbeit';

  // Screenshot-Paste
  const { handlePaste } = useScreenshotPaste((file, preview) => {
    setScreenshotFile(file);
    setScreenshotPreview(preview);
    toast.success('Screenshot eingefügt');
  });

  const handleScreenshot = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setScreenshotFile(file);
      const reader = new FileReader();
      reader.onload = () => setScreenshotPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSendFeedback = async () => {
    if (!feedbackText.trim()) return;
    setSending(true);
    const ok = await onFeedback(issue.id, feedbackText, feedbackTyp, screenshotFile || undefined);
    if (ok) {
      setFeedbackText('');
      setScreenshotFile(null);
      setScreenshotPreview(null);
      setShowFeedback(false);
    }
    setSending(false);
  };

  return (
    <div
      className="rounded-xl border overflow-hidden transition-shadow"
      style={{
        borderColor: isActive ? CI.emerald + '60' : expanded ? statusCfg.farbe + '40' : CI.border,
        boxShadow: expanded ? CI.shadowHover : CI.shadow,
        backgroundColor: index % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd,
        borderLeftWidth: isActive ? '3px' : '1px',
        borderLeftColor: isActive ? CI.emerald : undefined,
      }}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-3 px-3.5 py-3 text-left transition-colors"
        style={{ backgroundColor: 'transparent' }}
      >
        {/* Status-Indikator mit Leuchtpunkt */}
        <div className="flex-shrink-0 mt-0.5 relative">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: statusCfg.bg }}>
            <StatusIcon className="w-3.5 h-3.5" style={{ color: statusCfg.farbe }} />
          </div>
          {isActive && (
            <>
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CI.emerald }} />
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full animate-ping" style={{ backgroundColor: CI.emerald, opacity: 0.6 }} />
            </>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Labels-Zeile — klickbar */}
          <div className="flex items-center gap-1.5 flex-wrap mb-1" ref={dropdownRef} onClick={e => e.stopPropagation()}>
            {/* Status-Chip */}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === 'status' ? null : 'status'); }}
                className="text-[9px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5 transition-all hover:ring-1 hover:ring-offset-1"
                style={{ backgroundColor: statusCfg.bg, color: statusCfg.farbe, cursor: 'pointer' }}
                title="Status ändern"
              >
                <StatusIcon className="w-2.5 h-2.5" /> {statusCfg.label}
                {updating && <Loader2 className="w-2 h-2 animate-spin ml-0.5" />}
              </button>
              {openDropdown === 'status' && (
                <div className="absolute top-full left-0 mt-1 z-50 rounded-lg shadow-lg py-1 min-w-[100px]" style={{ backgroundColor: CI.bgPanel, border: `1px solid ${CI.border}` }}>
                  {(Object.entries(STATUS_CONFIG) as [RoadmapStatus, typeof STATUS_CONFIG[RoadmapStatus]][]).map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    return (
                      <button
                        key={key}
                        onClick={(e) => { e.stopPropagation(); handleFieldUpdate({ status: key }); }}
                        className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] transition-colors hover:bg-gray-50"
                        style={{ color: key === issue.status ? cfg.farbe : CI.textSecondary, fontWeight: key === issue.status ? 600 : 400 }}
                      >
                        <Icon className="w-3 h-3" style={{ color: cfg.farbe }} />
                        {cfg.label}
                        {key === issue.status && <Check className="w-3 h-3 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Priorität-Chip */}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === 'prio' ? null : 'prio'); }}
                className="text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5 transition-all hover:ring-1 hover:ring-offset-1"
                style={{ backgroundColor: prioCfg.bg, color: prioCfg.farbe, cursor: 'pointer' }}
                title="Priorität ändern"
              >
                {prioCfg.label}
              </button>
              {openDropdown === 'prio' && (
                <div className="absolute top-full left-0 mt-1 z-50 rounded-lg shadow-lg py-1 min-w-[90px]" style={{ backgroundColor: CI.bgPanel, border: `1px solid ${CI.border}` }}>
                  {(Object.entries(PRIO_CONFIG) as [RoadmapPrioritaet, typeof PRIO_CONFIG[RoadmapPrioritaet]][]).map(([key, cfg]) => (
                    <button
                      key={key}
                      onClick={(e) => { e.stopPropagation(); handleFieldUpdate({ prioritaet: key }); }}
                      className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] transition-colors hover:bg-gray-50"
                      style={{ color: key === issue.prioritaet ? cfg.farbe : CI.textSecondary, fontWeight: key === issue.prioritaet ? 600 : 400 }}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cfg.farbe }} />
                      {cfg.label}
                      {key === issue.prioritaet && <Check className="w-3 h-3 ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Typ-Chip */}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === 'typ' ? null : 'typ'); }}
                className="text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5 transition-all hover:ring-1 hover:ring-offset-1"
                style={{ backgroundColor: CI.bgSection, color: CI.textMuted, cursor: 'pointer' }}
                title="Typ ändern"
              >
                <TypIcon className="w-2.5 h-2.5" /> {typCfg.label}
              </button>
              {openDropdown === 'typ' && (
                <div className="absolute top-full left-0 mt-1 z-50 rounded-lg shadow-lg py-1 min-w-[100px]" style={{ backgroundColor: CI.bgPanel, border: `1px solid ${CI.border}` }}>
                  {(Object.entries(TYP_CONFIG) as [RoadmapTyp, typeof TYP_CONFIG[RoadmapTyp]][]).map(([key, cfg]) => {
                    const TIcon = cfg.icon;
                    return (
                      <button
                        key={key}
                        onClick={(e) => { e.stopPropagation(); handleFieldUpdate({ typ: key }); }}
                        className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] transition-colors hover:bg-gray-50"
                        style={{ color: key === issue.typ ? cfg.farbe : CI.textSecondary, fontWeight: key === issue.typ ? 600 : 400 }}
                      >
                        <TIcon className="w-3 h-3" style={{ color: cfg.farbe }} />
                        {cfg.label}
                        {key === issue.typ && <Check className="w-3 h-3 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Agent-Chip */}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === 'agent' ? null : 'agent'); }}
                className="text-[9px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5 transition-all hover:ring-1 hover:ring-offset-1"
                style={{ backgroundColor: agentCfg.bg, color: agentCfg.farbe, cursor: 'pointer' }}
                title="Agent ändern"
              >
                <AgentIcon className="w-2.5 h-2.5" /> {agentCfg.label}
              </button>
              {openDropdown === 'agent' && (
                <div className="absolute top-full left-0 mt-1 z-50 rounded-lg shadow-lg py-1 min-w-[110px]" style={{ backgroundColor: CI.bgPanel, border: `1px solid ${CI.border}` }}>
                  {(Object.entries(AGENT_CONFIG) as [string, typeof AGENT_CONFIG[string]][]).map(([key, cfg]) => {
                    const AIcon = cfg.icon;
                    return (
                      <button
                        key={key}
                        onClick={(e) => { e.stopPropagation(); handleFieldUpdate({ agent: key as 'manus' | 'claude-code' | 'unassigned' }); }}
                        className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] transition-colors hover:bg-gray-50"
                        style={{ color: key === issue.agent ? cfg.farbe : CI.textSecondary, fontWeight: key === issue.agent ? 600 : 400 }}
                      >
                        <AIcon className="w-3 h-3" style={{ color: cfg.farbe }} />
                        {cfg.label}
                        {key === issue.agent && <Check className="w-3 h-3 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {issue.feedbacks.length > 0 && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: CI.amberBg, color: CI.amber }}>
                {issue.feedbacks.length} Feedback{issue.feedbacks.length > 1 ? 's' : ''}
              </span>
            )}
            {issue.labels.some(l => l.name === 'ready-to-merge') && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: CI.emeraldBg, color: CI.emerald }}>
                Merge bereit
              </span>
            )}
            {issue.labels.some(l => l.name === 'human-feedback') && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: CI.amberBg, color: CI.amber }}>
                Entscheidung nötig
              </span>
            )}
            {issue.labels.some(l => l.name === 'needs-further-dev') && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: CI.redLight, color: CI.red }}>
                Nachbearbeitung
              </span>
            )}
          </div>

          {/* Titel — doppelklick zum Bearbeiten */}
          {editingTitle ? (
            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
              <input
                autoFocus
                value={titleDraft}
                onChange={e => setTitleDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleTitleSave(); if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(issue.title); } }}
                onBlur={handleTitleSave}
                className="text-xs font-medium leading-snug flex-1 px-1.5 py-0.5 rounded border focus:outline-none focus:ring-1"
                style={{ color: CI.textPrimary, borderColor: CI.border }}
              />
              <button onClick={handleTitleSave} className="p-0.5 rounded" style={{ color: CI.emerald }}>
                <Check className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => { setEditingTitle(false); setTitleDraft(issue.title); }} className="p-0.5 rounded" style={{ color: CI.textMuted }}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <p
              className="text-xs font-medium leading-snug cursor-text"
              style={{ color: CI.textPrimary }}
              onDoubleClick={(e) => { e.stopPropagation(); setEditingTitle(true); setTitleDraft(issue.title); }}
              title="Doppelklick zum Bearbeiten"
            >
              <span className="text-[10px] font-mono mr-1.5" style={{ color: CI.textMuted }}>#{issue.number}</span>
              {issue.title}
            </p>
          )}

          {/* Projekt + Fortschritt */}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] flex items-center gap-1" style={{ color: CI.textMuted }}>
              <ProjektIcon className="w-3 h-3" /> {projektName}
            </span>
            {(issue.fortschritt > 0 || isActive) && (
              <div className="flex items-center gap-1.5 flex-1">
                <div className="flex-1 max-w-24 h-1.5 rounded-full" style={{ backgroundColor: CI.bgSection }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.max(issue.fortschritt, isActive ? 5 : 0)}%`,
                      backgroundColor: issue.fortschritt === 100 ? CI.emerald : isActive ? CI.emerald : CI.blue,
                    }}
                  />
                </div>
                <span className="text-[9px] font-medium" style={{ color: issue.fortschritt === 100 ? CI.emerald : isActive ? CI.emerald : CI.blue }}>
                  {issue.fortschritt}%
                </span>
              </div>
            )}
            {/* Mikro-Zeitstempel */}
            <span className="text-[8px] font-mono ml-auto flex-shrink-0" style={{ color: CI.textLight, letterSpacing: '0.02em' }}>
              {(() => {
                const now = new Date();
                const updated = new Date(issue.updated_at);
                const created = new Date(issue.created_at);
                const diffMs = now.getTime() - updated.getTime();
                const diffMin = Math.floor(diffMs / 60000);
                const diffH = Math.floor(diffMin / 60);
                const diffD = Math.floor(diffH / 24);
                const relativ = diffMin < 1 ? 'jetzt' : diffMin < 60 ? `${diffMin}m` : diffH < 24 ? `${diffH}h` : diffD < 7 ? `${diffD}d` : updated.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
                const createdStr = created.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
                const timeStr = updated.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
                return diffMs < 86400000 ? `${timeStr} · ${relativ}` : `${createdStr} · ${relativ}`;
              })()}
            </span>
          </div>
        </div>

        <div className="flex-shrink-0 pt-1">
          {expanded ? <ChevronUp className="w-4 h-4" style={{ color: CI.textLight }} /> : <ChevronDown className="w-4 h-4" style={{ color: CI.textLight }} />}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-3.5 pb-3.5 space-y-3 pt-3" style={{ borderTop: `1px solid ${CI.borderLight}` }}>
          {/* Fragen-Block — wenn Fragen erkannt werden */}
          {(() => {
            const fragen = parseFragenAusBody(issue.body);
            if (fragen.length > 0 && issue.state !== 'closed') {
              return (
                <FragenBlock
                  fragen={fragen}
                  onSubmit={async (kommentar) => onReply(issue.id, kommentar)}
                  issueNumber={issue.number}
                />
              );
            }
            // Kein Fragen-Format → normaler Body
            if (issue.body) {
              return (
                <div className="text-xs whitespace-pre-wrap leading-relaxed rounded-lg p-3" style={{ backgroundColor: CI.bgPanel, color: CI.textSecondary, border: `1px solid ${CI.borderLight}` }}>
                  {issue.body.length > 400 ? issue.body.substring(0, 400) + '...' : issue.body}
                </div>
              );
            }
            return null;
          })()}

          {/* Kommentar-Thread */}
          {loadingKommentare && (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="w-3 h-3 animate-spin" style={{ color: CI.blue }} />
              <span className="text-[10px]" style={{ color: CI.textMuted }}>Kommentare laden…</span>
            </div>
          )}

          {!loadingKommentare && kommentare.length > 0 && (
            <div className="space-y-2">
              {kommentare.map(k => {
                const isClarifying = k.body.startsWith('🤔 **Clarifying Questions**');
                const isDone = k.body.startsWith('✅ Implementation complete');
                return (
                  <div
                    key={k.id}
                    className="text-[10px] rounded-lg p-2.5"
                    style={{
                      backgroundColor: isClarifying ? CI.blueBg : isDone ? CI.emeraldBg : CI.bgPanel,
                      color: isClarifying ? CI.blue : isDone ? CI.emerald : CI.textSecondary,
                      border: `1px solid ${isClarifying ? '#BFDBFE' : isDone ? '#BBF7D0' : CI.borderLight}`,
                    }}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="font-medium">{k.user}</span>
                      <span className="ml-auto" style={{ color: CI.textMuted }}>{new Date(k.created_at).toLocaleDateString('de-DE')}</span>
                    </div>
                    <p className="whitespace-pre-wrap leading-relaxed">{k.body}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Antwort-Formular bei offenen Rückfragen */}
          {hatOffeneFragen && (
            <div className="space-y-2">
              <textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Deine Antwort auf die Rückfragen…"
                rows={3}
                className="w-full text-xs rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2"
                style={{ backgroundColor: CI.bgPanel, color: CI.textPrimary, border: `1px solid ${CI.border}` }}
              />
              <button
                onClick={handleReply}
                disabled={!replyText.trim() || sendingReply}
                className="flex items-center gap-1.5 text-[10px] font-medium text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: CI.blue }}
              >
                {sendingReply ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Antworten
              </button>
            </div>
          )}

          {/* Checkliste */}
          {issue.checkliste.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: CI.textMuted }}>
                Checkliste ({issue.checkliste.filter(c => c.checked).length}/{issue.checkliste.length})
              </p>
              {issue.checkliste.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px]" style={{ color: item.checked ? CI.emerald : CI.textSecondary }}>
                  {item.checked ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> : <div className="w-3.5 h-3.5 rounded-sm border flex-shrink-0" style={{ borderColor: CI.textLight }} />}
                  <span className={item.checked ? 'line-through' : ''}>{item.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Feedback-Verlauf */}
          {issue.feedbacks.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: CI.textMuted }}>Feedback-Verlauf</p>
              {issue.feedbacks.map((fb, i) => (
                <div key={i} className="text-[10px] rounded-lg p-2.5" style={{
                  backgroundColor: fb.typ === 'bug' ? CI.redLight : fb.typ === 'tipp' ? CI.purpleBg : CI.blueBg,
                  color: fb.typ === 'bug' ? CI.red : fb.typ === 'tipp' ? CI.purple : CI.blue,
                  border: `1px solid ${fb.typ === 'bug' ? CI.redBorder : fb.typ === 'tipp' ? '#DDD6FE' : '#BFDBFE'}`,
                }}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <MessageCircle className="w-3 h-3" />
                    <span className="font-medium">{fb.typ === 'bug' ? 'Bug' : fb.typ === 'tipp' ? 'Tipp' : fb.typ === 'verbesserung' ? 'Verbesserung' : 'Feedback'}</span>
                    <span className="ml-auto" style={{ color: CI.textMuted }}>{new Date(fb.erstellt_am).toLocaleDateString('de-DE')}</span>
                  </div>
                  <p className="mt-1">{fb.text}</p>
                  {fb.screenshot_url && (
                    <img src={fb.screenshot_url} alt="Screenshot" className="mt-2 rounded-lg max-h-32 object-cover border" style={{ borderColor: CI.borderLight }} />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Aktions-Leiste */}
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={issue.html_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[10px] font-medium px-2.5 py-1.5 rounded-lg transition-colors"
              style={{ backgroundColor: CI.bgSection, color: CI.textSecondary }}
            >
              <Github className="w-3 h-3" /> GitHub <ExternalLink className="w-2.5 h-2.5" />
            </a>
            <button
              onClick={() => setShowFeedback(!showFeedback)}
              className="flex items-center gap-1.5 text-[10px] font-medium px-2.5 py-1.5 rounded-lg transition-colors"
              style={{ backgroundColor: showFeedback ? CI.redLight : CI.amberBg, color: showFeedback ? CI.red : CI.amber }}
            >
              <MessageCircle className="w-3 h-3" /> Feedback geben
            </button>
          </div>

          {/* Feedback-Formular mit Strg+V */}
          {showFeedback && (
            <div className="rounded-xl p-3 space-y-2.5" style={{ backgroundColor: CI.bgSection, border: `1px dashed ${CI.amber}` }}>
              <p className="text-[10px] font-semibold" style={{ color: CI.textPrimary }}>Feedback zu #{issue.number}</p>

              {/* Typ-Auswahl */}
              <div className="flex items-center gap-1.5">
                {(['feedback', 'bug', 'verbesserung', 'tipp'] as const).map(typ => (
                  <button
                    key={typ}
                    onClick={() => setFeedbackTyp(typ)}
                    className="text-[10px] px-2 py-1 rounded-lg transition-colors"
                    style={{
                      backgroundColor: feedbackTyp === typ ? (typ === 'bug' ? CI.redLight : typ === 'tipp' ? CI.purpleBg : CI.blueBg) : CI.bgPanel,
                      color: feedbackTyp === typ ? (typ === 'bug' ? CI.red : typ === 'tipp' ? CI.purple : CI.blue) : CI.textMuted,
                      fontWeight: feedbackTyp === typ ? 600 : 400,
                      border: `1px solid ${feedbackTyp === typ ? 'transparent' : CI.borderLight}`,
                    }}
                  >
                    {typ === 'feedback' ? 'Feedback' : typ === 'bug' ? 'Bug' : typ === 'verbesserung' ? 'Verbesserung' : 'Tipp'}
                  </button>
                ))}
              </div>

              {/* Text mit Strg+V */}
              <div className="relative">
                <textarea
                  value={feedbackText}
                  onChange={e => setFeedbackText(e.target.value)}
                  onPaste={handlePaste}
                  placeholder="Beschreibe dein Feedback... (Strg+V für Screenshot)"
                  rows={3}
                  className="w-full text-xs rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2"
                  style={{ backgroundColor: CI.bgPanel, color: CI.textPrimary, border: `1px solid ${CI.border}` }}
                />
                {!feedbackText && !screenshotPreview && (
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 text-[9px] pointer-events-none" style={{ color: CI.textLight }}>
                    <Clipboard className="w-3 h-3" /> Strg+V
                  </div>
                )}
              </div>

              {/* Screenshot */}
              <div className="flex items-center gap-2">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleScreenshot} className="hidden" />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1.5 text-[10px] px-2.5 py-1.5 rounded-lg transition-colors"
                  style={{ backgroundColor: CI.bgPanel, color: CI.textMuted, border: `1px solid ${CI.border}` }}
                >
                  <Camera className="w-3 h-3" /> Screenshot
                </button>
                {screenshotPreview && (
                  <div className="relative">
                    <img src={screenshotPreview} alt="Preview" className="h-8 rounded border" style={{ borderColor: CI.border }} />
                    <button
                      onClick={() => { setScreenshotFile(null); setScreenshotPreview(null); }}
                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: CI.red, color: 'white' }}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Senden */}
              <button
                onClick={handleSendFeedback}
                disabled={!feedbackText.trim() || sending}
                className="flex items-center gap-1.5 text-[10px] font-medium text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: CI.red }}
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Feedback senden
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// NEUES ISSUE FORMULAR (mit Strg+V)
// ═══════════════════════════════════════════════════════════════════════════

function NeuesIssueFormular({
  onSubmit,
}: {
  onSubmit: (projektId: string, title: string, body: string, typ: RoadmapTyp, prio: RoadmapPrioritaet, screenshotUrl?: string) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [projekt, setProjekt] = useState(GITHUB_PROJEKTE[0].id);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [typ, setTyp] = useState<RoadmapTyp>('feature');
  const [prio, setPrio] = useState<RoadmapPrioritaet>('medium');
  const [sending, setSending] = useState(false);
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Screenshot-Paste
  const { handlePaste } = useScreenshotPaste((file, preview) => {
    setScreenshotFile(file);
    setScreenshotPreview(preview);
    toast.success('Screenshot eingefügt');
  });

  const handleScreenshot = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setScreenshotFile(file);
      const reader = new FileReader();
      reader.onload = () => setScreenshotPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSending(true);

    // Upload screenshot to Supabase Storage if present
    let screenshotUrl: string | undefined;
    if (screenshotFile) {
      try {
        const fileName = `issues/${Date.now()}_${screenshotFile.name}`;
        const { error: uploadError } = await supabase.storage
          .from('dokumente')
          .upload(fileName, screenshotFile, { contentType: screenshotFile.type });

        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from('dokumente')
            .getPublicUrl(fileName);
          screenshotUrl = urlData?.publicUrl;
        } else {
          console.error('Screenshot Upload:', uploadError);
          toast.error('Screenshot-Upload fehlgeschlagen — Issue wird ohne Screenshot erstellt');
        }
      } catch (err) {
        console.error('Screenshot Upload:', err);
        toast.error('Screenshot-Upload fehlgeschlagen — Issue wird ohne Screenshot erstellt');
      }
    }

    const ok = await onSubmit(projekt, title, body, typ, prio, screenshotUrl);
    if (ok) {
      setTitle('');
      setBody('');
      setScreenshotFile(null);
      setScreenshotPreview(null);
      setOpen(false);
    }
    setSending(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-2 text-xs font-medium py-3 rounded-xl transition-colors"
        style={{ border: `2px dashed ${CI.red}40`, color: CI.red, backgroundColor: CI.redLight + '60' }}
      >
        <Plus className="w-4 h-4" /> Neues Feedback / Issue erstellen
      </button>
    );
  }

  return (
    <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: CI.red + '40', backgroundColor: CI.redLight + '30' }}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold flex items-center gap-2" style={{ color: CI.textPrimary }}>
          <Plus className="w-4 h-4" style={{ color: CI.red }} /> Neues Issue / Feedback
        </p>
        <button onClick={() => setOpen(false)} className="p-1 rounded-lg" style={{ color: CI.textMuted }}>
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Projekt */}
      <div>
        <label className="text-[10px] font-medium block mb-1" style={{ color: CI.textMuted }}>Projekt</label>
        <select
          value={projekt}
          onChange={e => setProjekt(e.target.value)}
          className="w-full text-xs rounded-lg px-3 py-2 focus:outline-none"
          style={{ backgroundColor: CI.bgPanel, color: CI.textPrimary, border: `1px solid ${CI.border}` }}
        >
          {GITHUB_PROJEKTE.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Titel */}
      <div>
        <label className="text-[10px] font-medium block mb-1" style={{ color: CI.textMuted }}>Titel</label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Kurze Beschreibung..."
          className="w-full text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-2"
          style={{ backgroundColor: CI.bgPanel, color: CI.textPrimary, border: `1px solid ${CI.border}` }}
        />
      </div>

      {/* Typ + Priorität */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-medium block mb-1" style={{ color: CI.textMuted }}>Typ</label>
          <div className="flex flex-wrap gap-1">
            {(Object.entries(TYP_CONFIG) as [RoadmapTyp, typeof TYP_CONFIG[RoadmapTyp]][]).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={key}
                  onClick={() => setTyp(key)}
                  className="text-[10px] px-2 py-1 rounded-lg flex items-center gap-1 transition-colors"
                  style={{
                    backgroundColor: typ === key ? cfg.farbe + '15' : CI.bgPanel,
                    color: typ === key ? cfg.farbe : CI.textMuted,
                    fontWeight: typ === key ? 600 : 400,
                    border: `1px solid ${typ === key ? cfg.farbe + '30' : CI.borderLight}`,
                  }}
                >
                  <Icon className="w-3 h-3" /> {cfg.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-[10px] font-medium block mb-1" style={{ color: CI.textMuted }}>Priorität</label>
          <div className="flex flex-wrap gap-1">
            {(Object.entries(PRIO_CONFIG) as [RoadmapPrioritaet, typeof PRIO_CONFIG[RoadmapPrioritaet]][]).map(([key, cfg]) => (
              <button
                key={key}
                onClick={() => setPrio(key)}
                className="text-[10px] px-2 py-1 rounded-lg transition-colors"
                style={{
                  backgroundColor: prio === key ? cfg.bg : CI.bgPanel,
                  color: prio === key ? cfg.farbe : CI.textMuted,
                  fontWeight: prio === key ? 600 : 400,
                  border: `1px solid ${prio === key ? cfg.farbe + '30' : CI.borderLight}`,
                }}
              >
                {cfg.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Beschreibung mit Strg+V */}
      <div>
        <label className="text-[10px] font-medium block mb-1" style={{ color: CI.textMuted }}>Beschreibung</label>
        <div className="relative">
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            onPaste={handlePaste}
            placeholder="Detaillierte Beschreibung... (Strg+V für Screenshot)"
            rows={4}
            className="w-full text-xs rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2"
            style={{ backgroundColor: CI.bgPanel, color: CI.textPrimary, border: `1px solid ${CI.border}` }}
          />
          {!body && !screenshotPreview && (
            <div className="absolute bottom-2 right-2 flex items-center gap-1 text-[9px] pointer-events-none" style={{ color: CI.textLight }}>
              <Clipboard className="w-3 h-3" /> Strg+V
            </div>
          )}
        </div>
      </div>

      {/* Screenshot */}
      <div className="flex items-center gap-2">
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleScreenshot} className="hidden" />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1.5 text-[10px] px-2.5 py-1.5 rounded-lg transition-colors"
          style={{ backgroundColor: CI.bgPanel, color: CI.textMuted, border: `1px solid ${CI.border}` }}
        >
          <Camera className="w-3 h-3" /> Screenshot anhängen
        </button>
        {screenshotPreview && (
          <div className="relative">
            <img src={screenshotPreview} alt="Preview" className="h-10 rounded border" style={{ borderColor: CI.border }} />
            <button
              onClick={() => { setScreenshotFile(null); setScreenshotPreview(null); }}
              className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: CI.red, color: 'white' }}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        )}
      </div>

      {/* Senden */}
      <button
        onClick={handleSubmit}
        disabled={!title.trim() || sending}
        className="w-full flex items-center justify-center gap-2 text-xs font-medium text-white py-2.5 rounded-xl transition-colors disabled:opacity-50"
        style={{ backgroundColor: CI.red }}
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Issue erstellen
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ERWEITERTE FILTER-LEISTE
// ═══════════════════════════════════════════════════════════════════════════

function ErweiterteFilter({
  selectedAgent,
  setSelectedAgent,
  selectedTyp,
  setSelectedTyp,
  selectedPrio,
  setSelectedPrio,
}: {
  selectedAgent: string;
  setSelectedAgent: (v: string) => void;
  selectedTyp: string;
  setSelectedTyp: (v: string) => void;
  selectedPrio: string;
  setSelectedPrio: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[10px] font-medium px-2.5 py-1.5 rounded-lg transition-colors"
        style={{
          backgroundColor: (selectedAgent !== 'alle' || selectedTyp !== 'alle' || selectedPrio !== 'alle') ? CI.redLight : CI.bgSection,
          color: (selectedAgent !== 'alle' || selectedTyp !== 'alle' || selectedPrio !== 'alle') ? CI.red : CI.textSecondary,
          border: `1px solid ${(selectedAgent !== 'alle' || selectedTyp !== 'alle' || selectedPrio !== 'alle') ? CI.red + '30' : CI.border}`,
        }}
      >
        <SlidersHorizontal className="w-3 h-3" />
        Filter
        {(selectedAgent !== 'alle' || selectedTyp !== 'alle' || selectedPrio !== 'alle') && (
          <span className="w-4 h-4 rounded-full text-[8px] flex items-center justify-center text-white" style={{ backgroundColor: CI.red }}>
            {[selectedAgent, selectedTyp, selectedPrio].filter(v => v !== 'alle').length}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-2 rounded-xl p-3 space-y-3" style={{ backgroundColor: CI.bgSection, border: `1px solid ${CI.border}` }}>
          {/* Agent-Filter */}
          <div>
            <p className="text-[9px] font-medium uppercase tracking-wider mb-1.5" style={{ color: CI.textMuted }}>Agent</p>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setSelectedAgent('alle')}
                className="text-[10px] px-2 py-1 rounded-lg"
                style={{ backgroundColor: selectedAgent === 'alle' ? CI.red : CI.bgPanel, color: selectedAgent === 'alle' ? 'white' : CI.textMuted, border: `1px solid ${selectedAgent === 'alle' ? CI.red : CI.borderLight}` }}
              >
                Alle
              </button>
              {Object.entries(AGENT_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedAgent(selectedAgent === key ? 'alle' : key)}
                    className="text-[10px] px-2 py-1 rounded-lg flex items-center gap-1"
                    style={{ backgroundColor: selectedAgent === key ? cfg.bg : CI.bgPanel, color: selectedAgent === key ? cfg.farbe : CI.textMuted, fontWeight: selectedAgent === key ? 600 : 400, border: `1px solid ${selectedAgent === key ? cfg.farbe + '30' : CI.borderLight}` }}
                  >
                    <Icon className="w-3 h-3" /> {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Typ-Filter */}
          <div>
            <p className="text-[9px] font-medium uppercase tracking-wider mb-1.5" style={{ color: CI.textMuted }}>Typ</p>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setSelectedTyp('alle')}
                className="text-[10px] px-2 py-1 rounded-lg"
                style={{ backgroundColor: selectedTyp === 'alle' ? CI.red : CI.bgPanel, color: selectedTyp === 'alle' ? 'white' : CI.textMuted, border: `1px solid ${selectedTyp === 'alle' ? CI.red : CI.borderLight}` }}
              >
                Alle
              </button>
              {Object.entries(TYP_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedTyp(selectedTyp === key ? 'alle' : key)}
                    className="text-[10px] px-2 py-1 rounded-lg flex items-center gap-1"
                    style={{ backgroundColor: selectedTyp === key ? cfg.farbe + '15' : CI.bgPanel, color: selectedTyp === key ? cfg.farbe : CI.textMuted, fontWeight: selectedTyp === key ? 600 : 400, border: `1px solid ${selectedTyp === key ? cfg.farbe + '30' : CI.borderLight}` }}
                  >
                    <Icon className="w-3 h-3" /> {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Priorität-Filter */}
          <div>
            <p className="text-[9px] font-medium uppercase tracking-wider mb-1.5" style={{ color: CI.textMuted }}>Priorität</p>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setSelectedPrio('alle')}
                className="text-[10px] px-2 py-1 rounded-lg"
                style={{ backgroundColor: selectedPrio === 'alle' ? CI.red : CI.bgPanel, color: selectedPrio === 'alle' ? 'white' : CI.textMuted, border: `1px solid ${selectedPrio === 'alle' ? CI.red : CI.borderLight}` }}
              >
                Alle
              </button>
              {Object.entries(PRIO_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setSelectedPrio(selectedPrio === key ? 'alle' : key)}
                  className="text-[10px] px-2 py-1 rounded-lg"
                  style={{ backgroundColor: selectedPrio === key ? cfg.bg : CI.bgPanel, color: selectedPrio === key ? cfg.farbe : CI.textMuted, fontWeight: selectedPrio === key ? 600 : 400, border: `1px solid ${selectedPrio === key ? cfg.farbe + '30' : CI.borderLight}` }}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          {/* Filter zurücksetzen */}
          {(selectedAgent !== 'alle' || selectedTyp !== 'alle' || selectedPrio !== 'alle') && (
            <button
              onClick={() => { setSelectedAgent('alle'); setSelectedTyp('alle'); setSelectedPrio('alle'); }}
              className="text-[10px] font-medium px-2.5 py-1.5 rounded-lg"
              style={{ color: CI.red, backgroundColor: CI.redLight }}
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HAUPTKOMPONENTE: RoadmapTab V3
// ═══════════════════════════════════════════════════════════════════════════

export default function RoadmapTabV2() {
  const {
    issues,
    allIssues,
    stats,
    loading,
    syncing,
    lastSyncAt,
    autoRefresh,
    setAutoRefresh,
    selectedProjekt,
    selectedStatus,
    setSelectedProjekt,
    setSelectedStatus,
    syncFromGitHub,
    addFeedback,
    createIssueFromFeedback,
    updateIssue,
    replyToIssue,
    fetchComments,
    PROJEKTE,
  } = useGitHubRoadmap();

  // Erweiterte Filter
  const [selectedAgent, setSelectedAgent] = useState('alle');
  const [selectedTyp, setSelectedTyp] = useState('alle');
  const [selectedPrio, setSelectedPrio] = useState('alle');

  // Erweiterte Filterung
  const filteredIssues = issues.filter(issue => {
    if (selectedAgent !== 'alle' && issue.agent !== selectedAgent) return false;
    if (selectedTyp !== 'alle' && issue.typ !== selectedTyp) return false;
    if (selectedPrio !== 'alle' && issue.prioritaet !== selectedPrio) return false;
    return true;
  });

  // Handlungsempfehlungen und Anforderungen
  const empfehlungen = generateHandlungsempfehlungen(allIssues || issues);
  const anforderungen = generateAnforderungen(allIssues || issues);

  // Aktive Projekte berechnen
  const aktiveProjekte = new Set((allIssues || issues).filter(i => i.status === 'in_arbeit').map(i => i.projekt));

  return (
    <div className="space-y-4">
      {/* ── Handlungsempfehlungen ── */}
      {empfehlungen.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: CI.textMuted }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: CI.amber }} /> Handlungsempfehlungen
          </p>
          {empfehlungen.map(emp => {
            const Icon = emp.icon;
            return (
              <div
                key={emp.id}
                className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all hover:shadow-md"
                style={{ backgroundColor: emp.bg, border: `1px solid ${emp.farbe}20` }}
                onClick={() => {
                  if (emp.projekt) setSelectedProjekt(emp.projekt);
                }}
              >
                <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: emp.farbe + '15' }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: emp.farbe }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] leading-snug" style={{ color: emp.farbe }}>
                    {emp.text}
                  </p>
                </div>
                <ArrowRight className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: emp.farbe + '60' }} />
              </div>
            );
          })}
        </div>
      )}

      {/* ── Anforderungen (Was Claude braucht) ── */}
      {anforderungen.length > 0 && (
        <div className="rounded-xl p-3 space-y-2" style={{ backgroundColor: CI.amberBg, border: `1px solid ${CI.amber}30` }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: CI.amber }}>
            <AlertTriangle className="w-3.5 h-3.5" /> Aktion erforderlich
          </p>
          {anforderungen.map(anf => {
            const Icon = anf.icon;
            return (
              <div key={anf.id} className="flex items-center gap-2 text-[11px] px-2.5 py-2 rounded-lg" style={{ backgroundColor: CI.bgPanel, border: `1px solid ${CI.border}` }}>
                <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: CI.amber }} />
                <span className="flex-1" style={{ color: CI.textSecondary }}>{anf.text}</span>
                <button className="text-[9px] px-2 py-0.5 rounded-md font-medium" style={{ backgroundColor: CI.amberBg, color: CI.amber }}>
                  Erledigen
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Status-Karten ── */}
      <div className="grid grid-cols-6 gap-1.5">
        {(Object.entries(STATUS_CONFIG) as [RoadmapStatus, typeof STATUS_CONFIG[RoadmapStatus]][]).map(([key, cfg]) => {
          const Icon = cfg.icon;
          const count =
            key === 'idee'           ? stats.ideen :
            key === 'geplant'        ? stats.geplant :
            key === 'in_arbeit'      ? stats.inArbeit :
            key === 'review'         ? stats.review :
            key === 'human_feedback' ? stats.humanFeedback :
            stats.fertig;
          const isActive = selectedStatus === key;
          return (
            <button
              key={key}
              onClick={() => setSelectedStatus(isActive ? 'alle' : key)}
              className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl transition-all"
              style={{
                backgroundColor: isActive ? cfg.bg : CI.bgPanel,
                border: `1px solid ${isActive ? cfg.farbe + '40' : CI.border}`,
                boxShadow: isActive ? CI.shadowHover : CI.shadow,
              }}
            >
              <Icon className="w-4 h-4" style={{ color: cfg.farbe }} />
              <span className="text-sm font-bold" style={{ color: isActive ? cfg.farbe : CI.textPrimary }}>{count}</span>
              <span className="text-[9px]" style={{ color: isActive ? cfg.farbe : CI.textMuted }}>{cfg.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── Projekt-Filter mit Leuchtpunkt ── */}
      <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide pb-1">
        <button
          onClick={() => setSelectedProjekt('alle')}
          className="text-[10px] font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap transition-colors flex-shrink-0"
          style={{
            backgroundColor: selectedProjekt === 'alle' ? CI.red : CI.bgSection,
            color: selectedProjekt === 'alle' ? 'white' : CI.textSecondary,
          }}
        >
          Alle ({stats.gesamt})
        </button>
        {stats.proProjekt.filter(p => p.count > 0).map(p => {
          const PIcon = PROJEKT_ICONS[p.id] || Rocket;
          const isActive = selectedProjekt === p.id;
          const isWorking = aktiveProjekte.has(p.id);
          return (
            <button
              key={p.id}
              onClick={() => setSelectedProjekt(isActive ? 'alle' : p.id)}
              className="text-[10px] font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap transition-colors flex items-center gap-1 flex-shrink-0 relative"
              style={{
                backgroundColor: isActive ? p.color + '15' : CI.bgSection,
                color: isActive ? p.color : CI.textSecondary,
                border: isActive ? `1px solid ${p.color}30` : '1px solid transparent',
              }}
            >
              <PIcon className="w-3 h-3" />
              {p.name.split(' ')[0]} ({p.count})
              {isWorking && (
                <span className="relative flex h-2 w-2 ml-0.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: CI.emerald }} />
                  <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: CI.emerald }} />
                </span>
              )}
            </button>
          );
        })}

        {/* Erweiterte Filter Button */}
        <div className="flex-shrink-0 ml-auto">
          <ErweiterteFilter
            selectedAgent={selectedAgent}
            setSelectedAgent={setSelectedAgent}
            selectedTyp={selectedTyp}
            setSelectedTyp={setSelectedTyp}
            selectedPrio={selectedPrio}
            setSelectedPrio={setSelectedPrio}
          />
        </div>
      </div>

      {/* ── Neues Issue / Feedback ── */}
      <NeuesIssueFormular onSubmit={createIssueFromFeedback} />

      {/* ── Issues-Liste ── */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: CI.red }} />
        </div>
      ) : filteredIssues.length === 0 ? (
        <div className="text-center py-12">
          <Github className="w-10 h-10 mx-auto mb-3" style={{ color: CI.textLight }} />
          <p className="text-sm font-medium" style={{ color: CI.textMuted }}>Keine Issues gefunden</p>
          <p className="text-xs mt-1" style={{ color: CI.textLight }}>
            {(selectedAgent !== 'alle' || selectedTyp !== 'alle' || selectedPrio !== 'alle')
              ? 'Versuche andere Filter-Einstellungen'
              : 'Synchronisiere mit GitHub oder erstelle ein neues Issue'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredIssues.map((issue, i) => (
            <IssueKarte key={issue.id} issue={issue} onFeedback={addFeedback} onUpdate={updateIssue} onFetchComments={fetchComments} onReply={replyToIssue} index={i} />
          ))}
        </div>
      )}

      {/* ── GitHub Sync Banner ── */}
      <div className="rounded-xl p-3" style={{ background: `linear-gradient(135deg, ${CI.bgSection}, ${CI.blueBg})`, border: `1px solid ${CI.border}` }}>
        <div className="flex items-start gap-2">
          <Github className={`w-4 h-4 flex-shrink-0 mt-0.5 ${syncing ? 'animate-spin' : ''}`} style={{ color: CI.textSecondary }} />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium" style={{ color: CI.textPrimary }}>GitHub Live-Sync</p>
                <p className="text-[10px] mt-0.5" style={{ color: CI.textMuted }}>
                  {PROJEKTE.length} Projekte &bull; {stats.gesamt} Issues &bull; {stats.inArbeit} in Arbeit &bull; {aktiveProjekte.size} aktive Projekte
                  {lastSyncAt && (
                    <> &bull; Letzter Sync: {lastSyncAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className="flex items-center gap-1 text-[10px] font-medium px-2 py-1.5 rounded-lg transition-colors"
                  style={{
                    backgroundColor: autoRefresh ? CI.emeraldBg : CI.bgSection,
                    color: autoRefresh ? CI.emerald : CI.textMuted,
                    border: `1px solid ${autoRefresh ? CI.emerald : CI.border}`,
                  }}
                  title={autoRefresh ? 'Auto-Refresh deaktivieren' : 'Auto-Refresh aktivieren (5 Min)'}
                >
                  {autoRefresh ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                  {autoRefresh ? 'Auto an' : 'Auto aus'}
                </button>
                <button
                  onClick={() => syncFromGitHub()}
                  disabled={syncing}
                  className="flex items-center gap-1.5 text-[10px] font-medium text-white disabled:opacity-50 px-3 py-1.5 rounded-lg transition-colors"
                  style={{ backgroundColor: CI.textSecondary }}
                >
                  {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  {syncing ? 'Sync...' : 'Sync'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Manus Dev-Agent Info ── */}
      <div className="rounded-xl p-3" style={{ backgroundColor: CI.purpleBg, border: `1px solid #DDD6FE` }}>
        <div className="flex items-start gap-2">
          <Bot className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: CI.purple }} />
          <div>
            <p className="text-[10px] font-medium" style={{ color: '#5B21B6' }}>Manus Dev-Agent aktiv</p>
            <p className="text-[10px] mt-0.5 leading-relaxed" style={{ color: CI.purple }}>
              Manus erstellt Issues mit Research und Tipps. Claude Code implementiert. Du gibst Feedback — der Agent prüft und gibt weitere Tipps. Screenshots mit Strg+V einfügen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
